/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type AccountMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyUint8Array,
  type WritableAccount,
} from '@solana/kit';
import { STAKE_POOL_PROGRAM_ADDRESS } from '../programs';
import { getAccountMetaFactory, type ResolvedAccount } from '../shared';

export const FINALIZE_REWARD_RATE_CHANGE_DISCRIMINATOR = 7;

export function getFinalizeRewardRateChangeDiscriminatorBytes() {
  return getU8Encoder().encode(FINALIZE_REWARD_RATE_CHANGE_DISCRIMINATOR);
}

export type FinalizeRewardRateChangeInstruction<
  TProgram extends string = typeof STAKE_POOL_PROGRAM_ADDRESS,
  TAccountPool extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountPool extends string
        ? WritableAccount<TAccountPool>
        : TAccountPool,
      ...TRemainingAccounts,
    ]
  >;

export type FinalizeRewardRateChangeInstructionData = { discriminator: number };

export type FinalizeRewardRateChangeInstructionDataArgs = {};

export function getFinalizeRewardRateChangeInstructionDataEncoder(): FixedSizeEncoder<FinalizeRewardRateChangeInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([['discriminator', getU8Encoder()]]),
    (value) => ({
      ...value,
      discriminator: FINALIZE_REWARD_RATE_CHANGE_DISCRIMINATOR,
    })
  );
}

export function getFinalizeRewardRateChangeInstructionDataDecoder(): FixedSizeDecoder<FinalizeRewardRateChangeInstructionData> {
  return getStructDecoder([['discriminator', getU8Decoder()]]);
}

export function getFinalizeRewardRateChangeInstructionDataCodec(): FixedSizeCodec<
  FinalizeRewardRateChangeInstructionDataArgs,
  FinalizeRewardRateChangeInstructionData
> {
  return combineCodec(
    getFinalizeRewardRateChangeInstructionDataEncoder(),
    getFinalizeRewardRateChangeInstructionDataDecoder()
  );
}

export type FinalizeRewardRateChangeInput<
  TAccountPool extends string = string,
> = {
  /** The stake pool */
  pool: Address<TAccountPool>;
};

export function getFinalizeRewardRateChangeInstruction<
  TAccountPool extends string,
  TProgramAddress extends Address = typeof STAKE_POOL_PROGRAM_ADDRESS,
>(
  input: FinalizeRewardRateChangeInput<TAccountPool>,
  config?: { programAddress?: TProgramAddress }
): FinalizeRewardRateChangeInstruction<TProgramAddress, TAccountPool> {
  // Program address.
  const programAddress = config?.programAddress ?? STAKE_POOL_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    pool: { value: input.pool ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [getAccountMeta(accounts.pool)],
    data: getFinalizeRewardRateChangeInstructionDataEncoder().encode({}),
    programAddress,
  } as FinalizeRewardRateChangeInstruction<TProgramAddress, TAccountPool>);
}

export type ParsedFinalizeRewardRateChangeInstruction<
  TProgram extends string = typeof STAKE_POOL_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** The stake pool */
    pool: TAccountMetas[0];
  };
  data: FinalizeRewardRateChangeInstructionData;
};

export function parseFinalizeRewardRateChangeInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>
): ParsedFinalizeRewardRateChangeInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 1) {
    // TODO: Coded error.
    throw new Error('Not enough accounts');
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: { pool: getNextAccount() },
    data: getFinalizeRewardRateChangeInstructionDataDecoder().decode(
      instruction.data
    ),
  };
}
